import { useAuthContext } from "../../contexts/auth-context";
import { useEffect, useRef, useState } from "react";
import type { KeyboardEventHandler, MouseEventHandler } from "react";
import { BodyMarkdown, Inbox, SuprSendProvider } from "@suprsend/react";
import type { CustomNotificationCard } from "@suprsend/react";
import { TInvitationDecision, TInvitationDisplayStatus, TProcessStates, TSuprSendInvitationExtraData } from "../../lib/types/general";
import { useRouteNavigation } from "../../lib/common/routes";
import { formatRelativeTime } from "../../lib/utils";
import { getInvitationStatus } from "../../services/notifications";
import { useNotificationContext } from "../../contexts/notification-context";

type TNotificationsV2Props = {
    showForSmallDevice: boolean;
    isLandingPage?: boolean;
    refreshSuprSendUserToken: () => Promise<string>;
};

type TSuprSendNotificationAction =
    | {
          type: "navigation";
          actionUrl: string;
      }
    | {
          type: "invitation";
          decision: TInvitationDecision;
          invitationId: number;
          fromUserId: number;
      };

type TSuprSendNotificationActionResult =
    | { type: "navigation" }
    | {
          type: "invitation";
          status: TInvitationDisplayStatus;
      };

type TSuprSendNotificationCardProps = CustomNotificationCard & {
    onAction: (action: TSuprSendNotificationAction) => Promise<TSuprSendNotificationActionResult>;
    getUserTokenId: () => Promise<string>;
};

const INVITATION_FINAL_MESSAGES: Record<TInvitationDisplayStatus, string> = {
    unread: "",
    accepted: "Invitation accepted",
    rejected: "Invitation declined",
    unavailable: "This invitation is no longer available",
};

const getInvitationData = (extraData: string): TSuprSendInvitationExtraData => JSON.parse(extraData) as TSuprSendInvitationExtraData;

const requestInvitationStatus = async (getUserTokenId: () => Promise<string>, invitationId: number): Promise<TInvitationDisplayStatus> => {
    const userTokenId = await getUserTokenId();
    return getInvitationStatus(userTokenId, invitationId);
};

export const SuprSendNotificationCard = ({
    notificationData,
    markAsRead,
    markAsUnread,
    markAsArchived,
    markAsInteracted,
    onAction,
    getUserTokenId,
}: TSuprSendNotificationCardProps) => {
    const message = notificationData.message;
    const hasInvitationTag = notificationData.tags?.includes("invitation") ?? false;
    const invitationData = hasInvitationTag ? getInvitationData(message.extra_data!) : null;
    const invitationId = invitationData?.invitationId;
    const isLegacyInvitation = hasInvitationTag && invitationId === undefined;
    const notificationActionUrl = message.url;
    const [invitationStatus, setInvitationStatus] = useState<TInvitationDisplayStatus | undefined>(isLegacyInvitation ? "unavailable" : undefined);
    const [workingState, setWorkingState] = useState<TProcessStates>(hasInvitationTag && !isLegacyInvitation ? "working" : "idle");
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [avatarFailed, setAvatarFailed] = useState(false);
    const invitationStatusRequest = useRef<Promise<TInvitationDisplayStatus>>();

    useEffect(() => {
        if (!invitationId) return;

        let isActive = true;
        const statusRequest = invitationStatusRequest.current ?? requestInvitationStatus(getUserTokenId, invitationId);
        invitationStatusRequest.current = statusRequest;

        const loadInvitationStatus = async () => {
            try {
                const currentStatus = await statusRequest;
                if (!isActive) return;
                setInvitationStatus(currentStatus);
                setWorkingState("done");
            } catch {
                if (!isActive) return;
                setWorkingState("error");
                setErrorMessage("We couldn't check this invitation. Please try again.");
            }
        };

        loadInvitationStatus();

        return () => {
            isActive = false;
        };
    }, [getUserTokenId]);

    const runAction = async (action: TSuprSendNotificationAction): Promise<void> => {
        if (workingState === "working") return;

        setErrorMessage(undefined);
        setWorkingState("working");
        let actionCompleted = false;

        try {
            const result = await onAction(action);
            actionCompleted = true;

            if (result.type === "invitation") {
                setInvitationStatus(result.status);
            }

            await markAsInteracted();
            setWorkingState("done");
        } catch {
            setWorkingState("error");
            setErrorMessage(
                actionCompleted
                    ? "The action was completed, but we couldn't update the notification."
                    : action.type === "invitation"
                      ? "We couldn't update this invitation. Please try again."
                      : "We couldn't complete this notification action. Please try again.",
            );
        }
    };

    const finalMessage = invitationStatus ? INVITATION_FINAL_MESSAGES[invitationStatus] : undefined;

    const runMenuAction = async (action: CustomNotificationCard["markAsRead"]) => {
        setIsMenuOpen(false);
        setErrorMessage(undefined);
        try {
            await action();
        } catch {
            setErrorMessage("We couldn't update this notification. Please try again.");
        }
    };

    const handleNotificationClick = async () => {
        if (!notificationActionUrl) return;

        await runAction({
            type: "navigation",
            actionUrl: notificationActionUrl,
        });
    };

    const handleNotificationKeyDown: KeyboardEventHandler<HTMLDivElement> = async (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        await handleNotificationClick();
    };

    const handleInvitationAction: MouseEventHandler<HTMLButtonElement> = async (event) => {
        event.stopPropagation();
        if (!invitationData?.invitationId) return;

        const decision = event.currentTarget.dataset.decision;
        if (decision !== "accepted" && decision !== "rejected") return;

        await runAction({
            type: "invitation",
            invitationId: invitationData.invitationId,
            fromUserId: invitationData.fromUserId,
            decision,
        });
    };

    const handleAvatarError = () => {
        setAvatarFailed(true);
    };

    const handleMenuToggle: MouseEventHandler<HTMLButtonElement> = (event) => {
        event.stopPropagation();
        setIsMenuOpen((current) => !current);
    };

    const handleReadStatusAction = async () => {
        await runMenuAction(notificationData.read_on ? markAsUnread : markAsRead);
    };

    const handleArchiveAction = async () => {
        await runMenuAction(markAsArchived);
    };

    const avatarUrl = message.avatar?.avatar_url;
    const notificationMessage = (
        <>
            {message.header && <div className="mt-2 small lh-sm fw-bold text-break">{message.header}</div>}
            <div className="suprsend-notification-card__body">
                <BodyMarkdown body={message.text} />
            </div>
            {message.subtext?.text && <div className="small text-secondary text-break">{message.subtext.text}</div>}
        </>
    );

    return (
        <div
            className={`position-relative p-3 border-bottom text-dark ${notificationData.read_on ? "bg-white" : "bg-primary bg-opacity-10"}`}
            onClick={(e) => e.stopPropagation()}
        >
            {notificationData.is_pinned && (
                <div className="d-flex align-items-center gap-1 mb-1 ms-5 small text-secondary">
                    <i className="suprsend-notification-card__pin-icon fa-solid fa-thumbtack" aria-hidden="true"></i>
                    <span>Pinned</span>
                </div>
            )}

            <div className="d-flex align-items-start justify-content-between">
                <div className="suprsend-notification-card__content d-flex flex-grow-1">
                    <div className="d-flex align-items-start gap-2 flex-shrink-0 mt-2">
                        <span
                            aria-label={notificationData.read_on ? "Read notification" : "Unread notification"}
                            className={`suprsend-notification-card__unread-indicator rounded-circle bg-info ${notificationData.read_on ? "invisible" : ""}`}
                        ></span>
                        {avatarUrl && !avatarFailed ? (
                            <img
                                src={avatarUrl}
                                alt=""
                                onError={handleAvatarError}
                                className="suprsend-notification-card__avatar rounded-circle object-fit-cover"
                            />
                        ) : (
                            <span className="suprsend-notification-card__avatar d-inline-flex align-items-center justify-content-center rounded-circle">
                                <i className="fa-solid fa-user" aria-hidden="true"></i>
                            </span>
                        )}
                    </div>

                    <div className="suprsend-notification-card__content flex-grow-1 mx-2">
                        {notificationActionUrl ? (
                            <div
                                role="button"
                                tabIndex={0}
                                aria-disabled={workingState === "working"}
                                className={`suprsend-notification-card__link ${workingState === "working" ? "suprsend-notification-card__link--disabled" : ""}`}
                                onClick={handleNotificationClick}
                                onKeyDown={handleNotificationKeyDown}
                            >
                                {notificationMessage}
                            </div>
                        ) : (
                            notificationMessage
                        )}

                        {hasInvitationTag && invitationStatus === undefined && workingState === "working" && (
                            <div className="d-flex align-items-center gap-2 mt-2 small text-secondary">
                                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <span>Checking invitation status</span>
                            </div>
                        )}
                        {hasInvitationTag && finalMessage && <div className="mt-2 small text-secondary fw-semibold">{finalMessage}</div>}
                        {hasInvitationTag && invitationStatus === "unread" && (
                            <div className="d-flex gap-2 flex-wrap mt-3 mb-1">
                                {message.actions?.map((action, index) => (
                                    <button
                                        key={`${action.name}-${index}`}
                                        type="button"
                                        disabled={workingState === "working"}
                                        data-decision={index === 0 ? "accepted" : "rejected"}
                                        onClick={handleInvitationAction}
                                        className={`suprsend-notification-card__action btn btn-sm fw-semibold ${
                                            index === 0 ? "btn-primary" : "btn-outline-primary"
                                        }`}
                                    >
                                        {workingState === "working" && index === 0 && (
                                            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                                        )}
                                        {action.name}
                                    </button>
                                ))}
                            </div>
                        )}
                        {notificationData.expiry && notificationData.is_expiry_visible && (
                            <span className="badge bg-light text-secondary fw-normal mt-2">Expires {new Date(notificationData.expiry).toLocaleString()}</span>
                        )}
                        {errorMessage && (
                            <div className="mt-2 small text-danger" role="alert">
                                {errorMessage}
                            </div>
                        )}
                    </div>
                </div>

                <div className="suprsend-notification-card__actions-menu d-flex flex-column align-items-center gap-1 flex-shrink-0 mt-1 text-dark">
                    <span className="small text-nowrap">{formatRelativeTime(notificationData.created_on)}</span>
                    <div className="position-relative">
                        <button
                            type="button"
                            className="btn border-0 p-1 rounded-circle  lh-1"
                            aria-label="Notification options"
                            aria-expanded={isMenuOpen}
                            onClick={handleMenuToggle}
                        >
                            <i className="fa-solid fa-ellipsis" aria-hidden="true"></i>
                        </button>
                        {isMenuOpen && (
                            <div
                                className="suprsend-notification-card__actions-dropdown position-absolute end-0 bg-white border rounded-3 shadow-sm py-1 z-2"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <button type="button" className="dropdown-item px-3 py-2 small" onClick={handleReadStatusAction}>
                                    <i className={`fa-regular ${notificationData.read_on ? "fa-envelope" : "fa-envelope-open"} me-2`} aria-hidden="true"></i>
                                    {notificationData.read_on ? "Mark as unread" : "Mark as read"}
                                </button>
                                {!notificationData.archived && (
                                    <button type="button" className="dropdown-item px-3 py-2 small text-danger" onClick={handleArchiveAction}>
                                        <i className="fa-solid fa-trash me-2 " aria-hidden="true"></i>
                                        Delete
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const NotificationsV2 = ({ showForSmallDevice, isLandingPage = false, refreshSuprSendUserToken }: TNotificationsV2Props) => {
    const { userInfo, isLoggedIn, getUserTokenId } = useAuthContext();
    const { acceptInvitationClickHandler, rejectInvitationClickHandler } = useNotificationContext();
    const publicApiKey = process.env.REACT_APP_SUPRSEND_PUBLIC_API_KEY;
    const canShowNotifications = isLoggedIn && userInfo && publicApiKey;

    const { navigateToRoute } = useRouteNavigation();

    const resolveURL = async (action: TSuprSendNotificationAction): Promise<TSuprSendNotificationActionResult> => {
        if (action.type === "navigation") {
            const [, route, userId] = action.actionUrl.split("::");

            switch (route) {
                case "dentalTechInfo":
                    navigateToRoute("dentalTechInfo", userId);
                    break;
                case "dentalLabInfo":
                    navigateToRoute("dentalLabInfo", userId);
                    break;
                // case "subscriptionSelection":
                //     navigateToRoute("subscriptionSelection");
                //     break;
                default:
                    throw new Error(`Unsupported SuprSend destination: ${route}`);
            }
            return { type: "navigation" };
        }

        switch (action.decision) {
            case "accepted":
                return {
                    type: "invitation",
                    status: await acceptInvitationClickHandler(action.fromUserId, action.invitationId),
                };
            case "rejected":
                return {
                    type: "invitation",
                    status: await rejectInvitationClickHandler(action.fromUserId, action.invitationId),
                };
        }
    };

    return (
        <>
            {canShowNotifications && (
                <SuprSendProvider
                    publicApiKey={publicApiKey}
                    distinctId={userInfo.guid}
                    userToken={userInfo.suprSend.userToken}
                    refreshUserToken={refreshSuprSendUserToken}
                >
                    <span className=" top-menu__suprsend">
                        <Inbox
                            notificationComponent={(props) => <SuprSendNotificationCard {...props} onAction={resolveURL} getUserTokenId={getUserTokenId} />}
                            headerRightComponent={
                                showForSmallDevice
                                    ? ({ closeInboxPopover }) => (
                                          <button
                                              type="button"
                                              aria-label="Close notifications"
                                              className="btn border-0 bg-light p-0 rounded-3 ms-3 chatTitle__iconButton"
                                              onClick={closeInboxPopover}
                                          >
                                              <i className="fa-solid fa-xmark"></i>
                                          </button>
                                      )
                                    : undefined
                            }
                            showUnreadCountOnTabs
                            badgeComponent={({ count }) => (
                                <span
                                    data-testid="notifications-alarm"
                                    className="badge bg-danger top-menu__icon-badge"
                                    aria-label={`${count} unread notifications`}
                                >
                                    {count > 99 ? "99+" : count}
                                </span>
                            )}
                            bellComponent={() => (
                                <span className="top-menu__icon-action nav-row rounded-3">
                                    <i
                                        data-testid="notifications-bell"
                                        className={`fa-regular fa-bell fs-4 ${isLandingPage ? "text-muted " : "text-white"}`}
                                    ></i>
                                </span>
                            )}
                        />
                    </span>
                </SuprSendProvider>
            )}
        </>
    );
};
